/**
 * Générateur — Automatisme : demi-heure, quart d'heure, trois quarts d'heure
 * Attendu ID : 1035 | Niveau : 6ème | Domaine : GM
 * Rubrique : AUTOMATISME (programme 2025) — « L'élève sait qu'une demi-heure
 * c'est 30 minutes, qu'un quart d'heure c'est 15 minutes, que trois quarts
 * d'heure c'est 45 minutes. »
 * Labels (cartes exerciseur) :
 *   - Demi-heure, quart d'heure, trois quarts
 *
 * ── Plan de cartes (2026-08-22, cadrage prof) ──────────────────────────────
 * TROIS faits fermés : demi-heure = 30 min ; quart d'heure = 15 min ;
 * trois quarts d'heure = 45 min — plus leurs réciproques. L'ERREUR CIBLE
 * est DÉCIMALE : demie → 50, quart → 25, trois quarts → 75 (la contamination
 * base 10, toujours présente dans les grilles directes).
 * Deux ponts assumés : la FRACTION (~20 % des QCM : « 1/4 h = ... min »,
 * l'attendu mobilise la fraction d'une grandeur) et la SOMME (~15 % :
 * « une demi-heure et un quart d'heure » = 45 min — le 1/2 + 1/4 = 3/4
 * incarné ; toujours UN SEUL trou en saisie, jamais deux autour d'un +).
 * HORS PÉRIMÈTRE : les compositions avec des heures entières (« une heure
 * et demie » = application), les problèmes d'heure limite, l'écriture 1,5 h.
 * Domaine minuscule assumé : TIRAGE SANS REMISE par format.
 */
import {
    randomChoice, shuffle, tirageSansRemise,
    makeQcm, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 1035;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'Demi-heure, quart d\'heure, trois quarts';

/** Les TROIS faits (domaine fermé). `cible` = l'erreur décimale. */
const FAITS = [
    { id: 'demi', mots: 'une demi-heure', min: 30, frac: '\\dfrac{1}{2}', cible: '50' },
    { id: 'quart', mots: 'un quart d\'heure', min: 15, frac: '\\dfrac{1}{4}', cible: '25' },
    { id: 'troisquarts', mots: 'trois quarts d\'heure', min: 45, frac: '\\dfrac{3}{4}', cible: '75' },
];

/** Les deux sommes du pont 1/2 + 1/4 = 3/4 (et 1/4 + 1/4 = 1/2). */
const SOMMES = [
    { a: 'une demi-heure', b: 'un quart d\'heure', res: 45 },
    { a: 'un quart d\'heure', b: 'un quart d\'heure', res: 30 },
];

const majuscule = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// ─── Format 1 : QCM ───────────────────────────────────────────────────────

const finirQcm = (bonne, dis, texte, sousType) => {
    const payload = makeQcm(
        [bonne, ...dis.map((c) => c.valeur)],
        bonne,
        texte,
        opts(L1),
        false,
    );
    payload.contenu = { ...payload.contenu, sousType };
    payload._meta.params = { sousType, distracteurs: dis };
    return payload;
};

/** Grille numérique d'un fait : l'erreur décimale TOUJOURS là + 2 tournants
 *  parmi les minutes des autres faits et 60. */
const grilleNumerique = (f) => {
    const tournants = FAITS.filter((x) => x.id !== f.id)
        .map((x) => ({ valeur: String(x.min), code: 'autre_fraction' }));
    tournants.push({ valeur: '60', code: 'heure_pleine' });
    return [{ valeur: f.cible, code: 'erreur_decimale' }, ...shuffle(tournants).slice(0, 2)];
};

const genQcm = () => {
    const r = Math.random();
    if (r < 0.4) {
        // direct : « Une demi-heure, c'est combien de minutes ? »
        const f = randomChoice(FAITS);
        return finirQcm(String(f.min), grilleNumerique(f),
            `<p><b>${majuscule(f.mots)}, c'est combien de minutes ?</b></p>`, 'direct');
    }
    if (r < 0.55) {
        // réciproque en mots : « 45 minutes, c'est... »
        const f = randomChoice(FAITS);
        const dis = shuffle([
            ...FAITS.filter((x) => x.id !== f.id).map((x) => ({ valeur: x.mots, code: 'autre_fraction' })),
            { valeur: 'une heure', code: 'heure_pleine' },
        ]).slice(0, 3);
        return finirQcm(f.mots, dis,
            `<p><b>${f.min} minutes, c'est...</b></p>`, 'reciproque');
    }
    if (r < 0.7) {
        // réciproque en FRACTION : « 30 minutes, c'est... » → 1/2 h
        // (demande prof 2026-08-22)
        const f = randomChoice(FAITS);
        const dis = shuffle([
            ...FAITS.filter((x) => x.id !== f.id).map((x) => ({ valeur: `$${x.frac}$ h`, code: 'autre_fraction' })),
            { valeur: '1 h', code: 'heure_pleine' },
        ]).slice(0, 3);
        return finirQcm(`$${f.frac}$ h`, dis,
            `<p><b>${f.min} minutes, c'est...</b></p>`, 'fraction_reciproque');
    }
    if (r < 0.85) {
        // le pont FRACTION : « 1/4 h = ... min »
        const f = randomChoice(FAITS);
        return finirQcm(String(f.min), grilleNumerique(f),
            `<p><b>Complète : $${f.frac}$ h = ... min</b></p>`, 'fraction');
    }
    // la SOMME : « une demi-heure et un quart d'heure, c'est... »
    const s = randomChoice(SOMMES);
    const dis = shuffle([
        { valeur: '50', code: 'erreur_decimale' },
        { valeur: String(s.res - 10), code: 'somme_fausse' },
        { valeur: '60', code: 'heure_pleine' },
        { valeur: String(s.res + 15), code: 'somme_fausse' },
    ]).filter((c) => c.valeur !== String(s.res)).slice(0, 3);
    return finirQcm(String(s.res), dis,
        `<p><b>${majuscule(s.a)} et ${s.b}, c'est combien de minutes ?</b></p>`, 'somme');
};

// ─── Format 2 : trous saisie ──────────────────────────────────────────────

/** Le mot à trou d'un fait (l'article reste dehors, le trou est le mot nu). */
const ligneMot = (f) => {
    if (f.id === 'demi') return `${f.min} min, c'est une {{demi}}-heure`;
    if (f.id === 'quart') return `${f.min} min, c'est un {{quart}} d'heure`;
    return `${f.min} min, c'est {{trois quarts}} d'heure`;
};

const genTrouSaisie = () => {
    const variante = randomChoice(['egalites', 'egalites', 'mots', 'somme']);
    if (variante === 'somme') {
        // UN SEUL trou (jamais deux autour d'un +, règle commutative)
        const s = randomChoice(SOMMES);
        return makeTexteTrou(
            `${majuscule(s.a)} et ${s.b} font {{${s.res}}} min`,
            '<p><b>Complète.</b></p>',
            { ...opts(L1), mode: 'saisie' },
        );
    }
    if (variante === 'mots') {
        const faits = shuffle([...FAITS]).slice(0, randomChoice([1, 2, 2]));
        return makeTexteTrou(
            faits.map(ligneMot).join('\n'),
            '<p><b>Complète avec le mot qui convient (demi, quart, trois quarts).</b></p>',
            { ...opts(L1), mode: 'saisie' },
        );
    }
    const faits = shuffle([...FAITS]).slice(0, randomChoice([1, 2, 2]));
    const lignes = faits.map((f) => (Math.random() < 0.5
        ? `${majuscule(f.mots)} = {{${f.min}}} min`
        : `{{${f.min}}} min = ${f.mots}`));
    return makeTexteTrou(
        lignes.join('\n'),
        '<p><b>Complète les égalités.</b></p>',
        { ...opts(L1), mode: 'saisie' },
    );
};

// ─── Format 3 : V/F (pool fermé étiqueté à la main) ───────────────────────

// Miroirs conservés (arbitrage prof récurrent).
const AFFIRMATIONS = [
    { t: 'Une demi-heure = 30 min', v: true },
    { t: 'Un quart d\'heure = 15 min', v: true },
    { t: 'Trois quarts d\'heure = 45 min', v: true },
    { t: '30 min = une demi-heure', v: true },
    { t: '15 min = un quart d\'heure', v: true },
    { t: 'Une demi-heure = 50 min', v: false },          // L'ERREUR DÉCIMALE
    { t: 'Un quart d\'heure = 25 min', v: false },       // L'ERREUR DÉCIMALE
    { t: 'Trois quarts d\'heure = 75 min', v: false },   // L'ERREUR DÉCIMALE
    { t: 'Un quart d\'heure = 45 min', v: false },
    { t: 'Trois quarts d\'heure = 35 min', v: false },
];

const genVraiFaux = () => {
    for (let essai = 0; essai < 30; essai++) {
        const tirage = shuffle([...AFFIRMATIONS]).slice(0, 4);
        const nbVraies = tirage.filter((a) => a.v).length;
        if (nbVraies < 1 || nbVraies > 3) continue;
        return makeVraiFaux(
            tirage.map((a) => a.t),
            tirage.map((a) => (a.v ? 0 : 1)),
            '<p><b>Vrai ou faux ?</b></p>',
            opts(L1),
        );
    }
    return makeVraiFaux(
        [AFFIRMATIONS[0].t, AFFIRMATIONS[5].t, AFFIRMATIONS[1].t, AFFIRMATIONS[6].t],
        [0, 1, 0, 1],
        '<p><b>Vrai ou faux ?</b></p>',
        opts(L1),
    );
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'TROIS faits fermés : 30 / 15 / 45 — plus leurs réciproques, la fraction et la somme',
        'L\'ERREUR CIBLE est DÉCIMALE : 50 / 25 / 75, toujours présente dans les grilles directes et fraction',
        'hors périmètre : « une heure et demie » (application), l\'écriture 1,5 h, les heures limites',
        'domaine fermé assumé : tirage sans remise par format',
    ],
    fracheure_qcm_unique: {
        support: 'direct, réciproque en mots, fraction (1/4 h), ou somme',
        fixes: { erreur_decimale: '50 / 25 / 75 — la contamination base 10' },
        tournants: 'les minutes des autres fractions + 60 (l\'heure pleine)',
        somme: 'jamais deux trous autour d\'un + (règle commutative) — le résultat seul',
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    fracheure_qcm_unique: genQcm,
    fracheure_texte_trou_saisie: genTrouSaisie,
    fracheure_tableau_vf: genVraiFaux,
};

const FORMAT_LABELS = {
    fracheure_qcm_unique: 'QCM',
    fracheure_texte_trou_saisie: 'Saisie',
    fracheure_tableau_vf: 'Vrai/Faux',
};

export const CATEGORIES = [
    {
        id: 'fracheure',
        label: L1,
        formatsList: Object.keys(GENERATORS).map((k) => ({ key: k, label: FORMAT_LABELS[k] || k })),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    // domaine minuscule (3 faits) : TIRAGE SANS REMISE par format
    const payload = tirageSansRemise(
        `gen_1035:${key}`,
        () => GENERATORS[key](),
        (p) => p.texte
            + JSON.stringify(p.contenu.phrase
                || (p.elements_additionnels?.elements?.[0]?.config?.lignes ?? ''))
            + JSON.stringify(p._meta.params?.sousType ?? ''),
        250,
    );
    payload._meta.generatorKey = key;
    return payload;
};
